import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Cuestionario } from './interfaces/cuestionario.interface';
import { CuestionariosService } from './services/cuestionarios.service';
import { MessageService } from 'primeng/api';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';
import { MatDialog } from '@angular/material/dialog';
import { TiposDialogComponent } from './tipos-dialog/tipos-dialog.component';
import { PreguntasDialogComponent } from './preguntas-dialog/preguntas-dialog.component';

@Component({
  selector: 'app-cuestionario-dialog',
  templateUrl: './cuestionario-dialog.component.html',
  providers: [MessageService]
})
export class CuestionarioDialogComponent implements OnInit, OnChanges {
  @ViewChild(TiposDialogComponent) tiposDialog!: TiposDialogComponent;
  @ViewChild(PreguntasDialogComponent) preguntasDialog!: PreguntasDialogComponent;
  @Input() displayDialog: boolean = false;
  @Input() cuestionario: Cuestionario | null = null;
  @Output() displayDialogChange = new EventEmitter<boolean>();
  @Output() onHide = new EventEmitter<{refresh: boolean, success?: boolean, error?: string}>();

  form: FormGroup;
  tipos: any[] = [];
  gestiones: any[] = [];
  loading: boolean = false;
  selectedCuestionario: Cuestionario | null = null;
  displayTiposDialog: boolean = false;
  displayPreguntasDialog: boolean = false;

  constructor(
    private fb: FormBuilder,
    private cuestionariosService: CuestionariosService,
    private messageService: MessageService,
    private dialog: MatDialog
  ) {
    this.form = this.fb.group({
      sClaveCues: ['', [Validators.required]],
      sDescripcion: ['', [Validators.required]],
      iTipo: [null, [Validators.required]],
      iGestion: [null, [Validators.required]],
      bTodos: [false]
    });
  }

  ngOnInit(): void {
    this.cargarTipos();
    this.cargarGestiones();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cuestionario'] && this.cuestionario) {
      this.cargarDatosCuestionario();
    } else if (changes['displayDialog'] && !this.displayDialog) {
      this.form.reset();
    }
  }

  openCustomizables(): void {
    this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: '1000px',
      height: '1000px',
      disableClose: true,
      panelClass: ['custom-dialog-zindex'],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: 'cmtbl_cuestionario',
        sClave: `${this.form.get('sClaveCues')?.value}`,
        sNombre: this.form.get('sDescripcion')?.value,
        sTipo: 'Opcion'
      }
    });
  }

  openTipos(): void {
    const cuestionarioActual = {
      ...this.cuestionario,
      iTipo: this.form.get('iTipo')?.value
    };
    this.selectedCuestionario = cuestionarioActual;
    this.displayTiposDialog = true;
  }

  openPreguntas(): void {
    const cuestionarioActual = {
      ...this.cuestionario,
      iTipo: this.form.get('iTipo')?.value
    };
    this.selectedCuestionario = cuestionarioActual;
    this.displayPreguntasDialog = true;
  }

  cargarDatosCuestionario(): void {
    if (this.cuestionario?.sClaveCues) {
      this.loading = true;
      this.cuestionariosService.getCuestionarioEditar(this.cuestionario.sClaveCues).subscribe({
        next: (response) => {
          if (response.success && response.data.length > 0) {
            const cuestionario = response.data[0];
            this.form.patchValue({
              sClaveCues: cuestionario.sClaveCues,
              sDescripcion: cuestionario.sDescripcion,
              iTipo: Number(cuestionario.iTipo),
              iGestion: Number(cuestionario.iGestion),
              bTodos: cuestionario.bTodos === 1
            });
          }
        },
        error: (error) => {
          console.error('Error al cargar datos del cuestionario:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los datos del cuestionario'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    }
  }

  cargarTipos(): void {
    this.cuestionariosService.getOpcionesVariables({
      sTabla: 'cmtbl_cuestionario',
      sCampo: 'iTipo',
      iIdioma: 0
    }).subscribe({
      next: (response) => {
        if (response.success) {
          this.tipos = response.data.map((item) => ({
            label: item.sOpcion,
            value: Number(item.sValor)
          }));
        }
      },
      error: (error) => {
        console.error('Error al cargar tipos:', error);
      }
    });
  }

  cargarGestiones(): void {
    this.cuestionariosService.getOpcionesVariables({
      sTabla: 'cmtbl_cuestionario',
      sCampo: 'iGestion',
      iIdioma: 0
    }).subscribe({
      next: (response) => {
        if (response.success) {
          this.gestiones = response.data.map((item) => ({
            label: item.sOpcion,
            value: Number(item.sValor)
          }));
        }
      },
      error: (error) => {
        console.error('Error al cargar gestiones:', error);
      }
    });
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.loading = true;
      const formValue = this.form.value;
      
      const request = {
        que: this.cuestionario ? 2 : 1,
        sClave: formValue.sClaveCues,
        sDescripcion: formValue.sDescripcion,
        iTipo: formValue.iTipo,
        iGestion: formValue.iGestion,
        bTodos: formValue.bTodos ? 1 : 0
      };

      const serviceCall = this.cuestionario 
        ? this.cuestionariosService.actualizarCuestionario(request)
        : this.cuestionariosService.eliminarCuestionario(request);

      serviceCall.subscribe({
        next: (response) => {
          if (response.success && response.data.error === 0) {
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: this.cuestionario ? 'Cuestionario actualizado correctamente' : 'Cuestionario guardado correctamente'
            });
            this.onHide.emit({ refresh: true, success: true });
            this.displayDialog = false;
            this.displayDialogChange.emit(this.displayDialog);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: this.cuestionario ? 'Error al actualizar el cuestionario' : 'Error al guardar el cuestionario'
            });
            this.onHide.emit({ 
              refresh: false, 
              success: false, 
              error: this.cuestionario ? 'Error al actualizar el cuestionario' : 'Error al guardar el cuestionario'
            });
          }
        },
        error: (error) => {
          console.error('Error al guardar:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.cuestionario ? 'Error al actualizar el cuestionario' : 'Error al guardar el cuestionario'
          });
          this.onHide.emit({ 
            refresh: false, 
            success: false, 
            error: this.cuestionario ? 'Error al actualizar el cuestionario' : 'Error al guardar el cuestionario'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      this.onHide.emit({ 
        refresh: false, 
        success: false, 
        error: 'Por favor complete todos los campos requeridos' 
      });
    }
  }

  onDialogClosed(event?: {refresh: boolean, success?: boolean, error?: string}): void {
    this.displayDialog = false;
    this.displayDialogChange.emit(this.displayDialog);
    if (event) {
      this.onHide.emit(event);
    } else {
      this.onHide.emit({ refresh: false });
    }
  }

  onTiposDialogClosed(event?: {refresh: boolean, success?: boolean, error?: string}): void {
    this.displayTiposDialog = false;
    if (event?.refresh) {
      this.cargarDatosCuestionario();
    }
  }

  onPreguntasDialogClosed(event?: {refresh: boolean}): void {
    this.displayPreguntasDialog = false;
  }
} 